import produce from 'immer';
import { getFeatureFlag } from 'services/featureFlag';
import { create } from 'zustand';

export type FeatureFlag = {
  name: string;
  isEnabled: boolean;
};

export type FeatureFlagState = {
  flags: FeatureFlag[];
  isLoading: boolean;
  isError: boolean;
  isSuccessful: boolean;
};

export type FeatureFlagActions = {
  fetchFeatureFlags: () => Promise<void>;
  refreshFeatureFlags: () => Promise<void>;
  refreshSingleFeatureFlag: (flagName: FeatureFlags) => Promise<void>;
  setState: (newState: Partial<FeatureFlagState>) => void;
  isFeatureFlagEnabled: (flagName: FeatureFlags) => boolean;
};

export type FeatureFlagStore = FeatureFlagState & FeatureFlagActions;

export enum FeatureFlags {
  DeskManagerServiceRequestsOnECommerce = 'DeskManagerServiceRequestsOnECommerce',
}

const initialFeatureFlagState: FeatureFlagState = {
  flags: [
    {
      name: FeatureFlags.DeskManagerServiceRequestsOnECommerce,
      isEnabled: false,
    },
  ],
  isLoading: false,
  isError: false,
  isSuccessful: false,
};

const predefinedFlags = Object.values(FeatureFlags);

export const useFeatureFlagStore = create<FeatureFlagStore>()((set, get) => ({
  ...initialFeatureFlagState,

  setState: (newState: Partial<FeatureFlagState>) => {
    set(
      produce((state: FeatureFlagStore) => {
        Object.assign(state, newState);
      }),
    );
  },

  fetchFeatureFlags: async () => {
    const { setState } = get();

    setState({ isLoading: true });

    try {
      const flagResults = await Promise.allSettled(
        predefinedFlags.map((flag) => getFeatureFlag({ flag })),
      );

      const flags = flagResults.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        }
        console.warn(
          `Failed to fetch feature flag ${predefinedFlags[index]}:`,
          result.reason,
        );
        return {
          name: predefinedFlags[index],
          isEnabled: false,
        };
      });

      setState({
        flags,
        isError: false,
        isLoading: false,
        isSuccessful: true,
      });
    } catch (error) {
      setState({
        flags: initialFeatureFlagState.flags,
        isError: true,
        isLoading: false,
        isSuccessful: false,
      });
    }
  },

  refreshFeatureFlags: async () => {
    const { fetchFeatureFlags } = get();
    await fetchFeatureFlags();
  },

  refreshSingleFeatureFlag: async (flagName: FeatureFlags) => {
    const { setState, flags } = get();

    try {
      const updatedFlag = await getFeatureFlag({ flag: flagName });
      const updatedFlags = flags.map((flag) =>
        flag.name === flagName ? updatedFlag : flag,
      );

      setState({
        flags: updatedFlags,
        isError: false,
        isLoading: false,
        isSuccessful: true,
      });
    } catch (error) {
      setState({
        isError: true,
        isLoading: false,
        isSuccessful: false,
      });
    }
  },

  isFeatureFlagEnabled: (flagName): boolean => {
    const { flags } = get();
    const flag = flags?.find((it) => it.name === flagName);
    return flag?.isEnabled ?? false;
  },
}));
